/**
 * Shared logo-placement logic for the frontend (admin WYSIWYG editor + V2 PDF
 * preview). Mirrors the backend `services/brandHeader.ts` so the on-screen
 * preview matches the exported PDF exactly.
 *
 * Placement is stored as PERCENTAGES relative to a full-width header band:
 *   width → logo width as % of band/content width
 *   x     → logo left edge as % within the band
 *   y     → logo top edge as % within the band
 */

export interface LogoPlacement {
  width: number;
  x: number;
  y: number;
}

export interface BrandingPlacementInput {
  logoPosition?: 'left' | 'center' | 'right' | null;
  logoWidth?: number | null;
  logoX?: number | null;
  logoY?: number | null;
}

/** Default logo width (% of content width). */
export const DEFAULT_LOGO_WIDTH_PCT = 25;

/** Height of the header band, in px, in PDF/screen space (matches backend). */
export const BRAND_BAND_HEIGHT_PX = 70;

const clampPct = (v: unknown, fallback: number): number => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, n));
};

/**
 * Resolve a concrete {width,x,y} placement from stored branding, falling back
 * to the legacy left/center/right position when free placement is unset.
 */
export const resolveLogoPlacement = (branding: BrandingPlacementInput): LogoPlacement => {
  const hasFree =
    branding.logoWidth != null || branding.logoX != null || branding.logoY != null;

  const width = clampPct(branding.logoWidth, DEFAULT_LOGO_WIDTH_PCT);

  if (hasFree) {
    return {
      width,
      x: clampPct(branding.logoX, 0),
      y: clampPct(branding.logoY, 0),
    };
  }

  const position = branding.logoPosition || 'right';
  const x =
    position === 'left' ? 0 :
    position === 'center' ? Math.max(0, (100 - width) / 2) :
    Math.max(0, 100 - width); // right
  return { width, x, y: 0 };
};
